import React, { useState, useEffect, useRef } from "react";

interface InViewLoaderProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  rootMargin?: string;
  threshold?: number;
  id?: string;
}

export function InViewLoader({
  children,
  fallback = <div className="min-h-[200px] w-full animate-pulse bg-muted/20" />,
  rootMargin = "1200px",
  threshold = 0,
  id,
}: InViewLoaderProps) {
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const currentRef = containerRef.current;
    
    // If IntersectionObserver is not supported, just render the content
    if (typeof IntersectionObserver === "undefined") {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          // Once it's in view, we don't need to observe anymore
          if (currentRef) observer.unobserve(currentRef);
        }
      },
      { rootMargin, threshold }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [rootMargin, threshold]);

  return (
    <div ref={containerRef} id={id}>
      {isInView ? children : fallback}
    </div>
  );
}
